import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, UserPlus, Link2, Link2Off, Check } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { useDialog } from '../../../components/DialogProvider';
import { ApiError } from '../../../lib/api';
import { atendimentoApi } from '../lib/api';
import { useListaTeclado } from '../../../lib/useListaTeclado';
import { UpperInput } from '../../../components/inputs/UpperInput';
import type { ClienteResumo, Contato, Conversa } from '../lib/tipos';

interface Props {
  aberto: boolean;
  conversa: Conversa | null;
  onFechar: () => void;
  onAtualizado?: (contato: Contato) => void;
}

type Modo = 'buscar' | 'novo';

function formatarDoc(s: string | null): string {
  return s ?? '';
}

function digits(s: string): string {
  return s.replace(/\D/g, '');
}

export function ModalSalvarCliente({ aberto, conversa, onFechar, onAtualizado }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  const ct = conversa?.contato ?? null;

  const [modo, setModo] = useState<Modo>('buscar');
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ClienteResumo[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [vinculando, setVinculando] = useState<number | null>(null);
  const [vinculado, setVinculado] = useState<ClienteResumo | null>(null);
  const [carregandoAtual, setCarregandoAtual] = useState(false);

  // Form de cadastro novo
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [salvandoNovo, setSalvandoNovo] = useState(false);
  // Cliente que ja usa este WhatsApp — preenchido quando o backend recusa o cadastro (409).
  const [conflito, setConflito] = useState<ClienteResumo | null>(null);
  // Clientes que ja usam este numero (match por telefone/WhatsApp, igual ao cabecalho
  // do chat) — sugeridos proativamente ao abrir pra evitar duplicar cadastro.
  const [sugestoes, setSugestoes] = useState<ClienteResumo[]>([]);

  // Carrega cliente vinculado quando abre
  useEffect(() => {
    if (!aberto || !ct) return;
    setModo('buscar');
    setBusca('');
    setResultados([]);
    setConflito(null);
    setSugestoes([]);
    if (!ct.cliente_id) {
      setVinculado(null);
      // Pre-preenche form de cadastro com dados do contato
      const nomeCompleto = [ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || '';
      setNome(nomeCompleto);
      setTelefone(ct.telefone ?? '');
      setEmail(ct.email ?? '');
      // Sugestao proativa: clientes que ja usam este numero (mesmo match do
      // cabecalho do chat) — surge ao abrir pra evitar criar cliente duplicado.
      let cancelSug = false;
      atendimentoApi.obterConversa(conversa!.id)
        .then(async (cv) => {
          const matches = cv.contato?.clientes ?? [];
          if (!matches.length) return;
          const full = await Promise.all(
            matches.slice(0, 5).map((m) => atendimentoApi.obterCliente(m.id).catch(() => null)),
          );
          if (!cancelSug) setSugestoes(full.filter((x): x is ClienteResumo => !!x));
        })
        .catch(() => {});
      return () => { cancelSug = true; };
    }
    let cancelado = false;
    setCarregandoAtual(true);
    atendimentoApi.obterCliente(ct.cliente_id)
      .then((c) => { if (!cancelado) setVinculado(c); })
      .catch(() => { if (!cancelado) setVinculado(null); })
      .finally(() => { if (!cancelado) setCarregandoAtual(false); });
    return () => { cancelado = true; };
  }, [aberto, ct?.id, ct?.cliente_id]);

  // Busca debounced
  useEffect(() => {
    if (!aberto || vinculado) return;
    const q = busca.trim();
    if (q.length < 2) {
      setResultados([]);
      return;
    }
    let cancelado = false;
    setBuscando(true);
    const t = setTimeout(() => {
      atendimentoApi.buscarClientes(q)
        .then((lista) => { if (!cancelado) setResultados(lista); })
        .catch(() => { if (!cancelado) setResultados([]); })
        .finally(() => { if (!cancelado) setBuscando(false); });
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [busca, aberto, vinculado]);

  const podeCadastrarNovo = useMemo(() => nome.trim().length >= 2, [nome]);
  const teclado = useListaTeclado(resultados, (c) => vincular(c.id));

  async function vincular(clienteId: number) {
    if (!ct) return;
    setVinculando(clienteId);
    try {
      const atualizado = await atendimentoApi.atualizarContato(ct.id, { cliente_id: clienteId });
      onAtualizado?.(atualizado);
      const c = resultados.find((r) => r.id === clienteId)
        ?? sugestoes.find((s) => s.id === clienteId)
        ?? (conflito && conflito.id === clienteId ? conflito : null);
      setVinculado(c);
      toast.sucesso('Contato vinculado ao cliente');
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao vincular');
    } finally {
      setVinculando(null);
    }
  }

  async function desvincular() {
    if (!ct) return;
    const ok = await dialog.confirmar('Desvincular este contato do cliente?', {
      titulo: 'Desvincular cliente?',
      textoConfirmar: 'Desvincular',
      perigoso: true,
    });
    if (!ok) return;
    setVinculando(-1);
    try {
      const atualizado = await atendimentoApi.atualizarContato(ct.id, { cliente_id: null });
      onAtualizado?.(atualizado);
      setVinculado(null);
      toast.sucesso('Contato desvinculado');
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao desvincular');
    } finally {
      setVinculando(null);
    }
  }

  async function cadastrarNovo() {
    if (!ct || !podeCadastrarNovo) return;
    setSalvandoNovo(true);
    try {
      const tel = telefone.trim() || ct.telefone || ('+' + ct.jid?.replace(/@.*/, ''));
      const novo = await atendimentoApi.criarClienteDoContato({
        nome: nome.trim(),
        telefone: tel || null,
        whatsapp: digits(tel) ? tel : null,
        email: email.trim() || null,
      });
      const atualizado = await atendimentoApi.atualizarContato(ct.id, { cliente_id: novo.id });
      onAtualizado?.(atualizado);
      setVinculado(novo);
      toast.sucesso('Cliente cadastrado e vinculado');
    } catch (e: any) {
      // WhatsApp/telefone ja pertence a outro cliente — mostra ele pra atendente reaproveitar.
      const corpo = e instanceof ApiError ? e.corpo : null;
      if (e?.status === 409 && corpo?.erro === 'whatsapp_duplicado' && corpo.cliente_existente) {
        try {
          setConflito(await atendimentoApi.obterCliente(corpo.cliente_existente.id));
        } catch {
          setConflito(corpo.cliente_existente as ClienteResumo);
        }
      } else {
        toast.erro(e?.message ?? 'Falha ao cadastrar');
      }
    } finally {
      setSalvandoNovo(false);
    }
  }

  if (!aberto || !ct) return null;

  const nomeContato = [ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || ct.jid;

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Salvar como cliente"
      largura={520}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}
    >
      <div className="mb-3 p-2.5 rounded-lg flex items-start justify-between gap-2" style={{ backgroundColor: 'rgba(0,0,0,0.04)', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide opacity-60 mb-0.5">Contato</div>
          <div className="text-sm font-medium truncate">{nomeContato}</div>
          {ct.telefone && <div className="text-xs opacity-70">{ct.telefone}</div>}
          {!carregandoAtual && !vinculado && (
            <div className="text-[11px] mt-1 opacity-55">Sem cliente vinculado — nada a desvincular</div>
          )}
        </div>
        {vinculado && (
          <button
            type="button"
            onClick={desvincular}
            disabled={vinculando !== null}
            title="Desvincular este contato do cliente"
            className="flex-shrink-0 px-2.5 py-1.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
            style={{ borderColor: '#fca5a5', color: '#991b1b', backgroundColor: 'rgba(254,226,226,0.7)' }}
          >
            {vinculando === -1 ? <Loader2 size={13} className="animate-spin" /> : <Link2Off size={13} />}
            Desvincular
          </button>
        )}
      </div>

      {carregandoAtual && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={18} className="animate-spin" />
        </div>
      )}

      {!carregandoAtual && vinculado && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#fde68a', border: '1px solid #fbbf24' }}>
            <div className="flex items-center gap-1.5 text-xs font-medium text-black/70 mb-1">
              <Link2 size={12} /> Vinculado a cliente
            </div>
            <div className="text-sm font-medium text-black">{vinculado.nome}</div>
            <div className="text-xs text-black/70 space-y-0.5 mt-1">
              {vinculado.codigo_legado && <div>Codigo: {vinculado.codigo_legado}</div>}
              {vinculado.cpf_cnpj && <div>CPF/CNPJ: {formatarDoc(vinculado.cpf_cnpj)}</div>}
              {vinculado.telefone && <div>Tel: {vinculado.telefone}</div>}
              {(vinculado.cidade || vinculado.uf) && <div>{[vinculado.cidade, vinculado.uf].filter(Boolean).join(' / ')}</div>}
            </div>
          </div>
          <p className="text-[11px] opacity-60">Pra trocar de cliente, toque em <b>Desvincular</b> no topo e busque/cadastre outro.</p>
        </div>
      )}

      {!carregandoAtual && !vinculado && (
        <>
          {sugestoes.length > 0 && !conflito && (
            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-1.5 text-xs font-medium opacity-70">
                <Link2 size={12} /> Este WhatsApp ja pertence a {sugestoes.length > 1 ? 'clientes cadastrados' : 'um cliente'}
              </div>
              {sugestoes.map((c) => (
                <div key={c.id} className="p-3 rounded-lg" style={{ backgroundColor: '#fde68a', border: '1px solid #fbbf24' }}>
                  <div className="text-sm font-medium text-black">{c.nome}</div>
                  <div className="text-xs text-black/70 space-y-0.5 mt-1">
                    {c.codigo_legado && <div>Codigo: {c.codigo_legado}</div>}
                    {c.cpf_cnpj && <div>CPF/CNPJ: {formatarDoc(c.cpf_cnpj)}</div>}
                    {(c.telefone || c.whatsapp) && <div>Tel: {c.telefone || c.whatsapp}</div>}
                    {(c.cidade || c.uf) && <div>{[c.cidade, c.uf].filter(Boolean).join(' / ')}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => vincular(c.id)}
                    disabled={vinculando !== null}
                    className="mt-2 w-full py-2 rounded-lg btn-primario flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {vinculando === c.id ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                    Usar este cliente
                  </button>
                </div>
              ))}
              <p className="text-[11px] opacity-60">Pra evitar cliente duplicado, vincule ao cadastro que ja existe. Se nao for, busque ou cadastre abaixo.</p>
            </div>
          )}
          <div className="flex gap-1.5 mb-3 p-1 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => { setModo('buscar'); setConflito(null); }}
              className="flex-1 py-1.5 px-3 rounded text-sm font-medium transition flex items-center justify-center gap-1.5"
              style={{
                backgroundColor: modo === 'buscar' ? 'var(--panel-bg, #fff)' : 'transparent',
                color: modo === 'buscar' ? 'var(--page-fg, #000)' : 'inherit',
                boxShadow: modo === 'buscar' ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
              }}
            >
              <Search size={14} /> Buscar existente
            </button>
            <button
              type="button"
              onClick={() => { setModo('novo'); setConflito(null); }}
              className="flex-1 py-1.5 px-3 rounded text-sm font-medium transition flex items-center justify-center gap-1.5"
              style={{
                backgroundColor: modo === 'novo' ? 'var(--panel-bg, #fff)' : 'transparent',
                color: modo === 'novo' ? 'var(--page-fg, #000)' : 'inherit',
                boxShadow: modo === 'novo' ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
              }}
            >
              <UserPlus size={14} /> Cadastrar novo
            </button>
          </div>

          {modo === 'buscar' && (
            <>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  onKeyDown={teclado.onKeyDown}
                  placeholder="Nome, CPF/CNPJ, telefone, codigo..."
                  autoFocus
                  className="w-full pl-8 pr-3 py-1.5 rounded border text-sm bg-white/5 outline-none focus:ring-2 focus:ring-blue-400"
                  style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                />
              </div>

              {buscando && (
                <div className="flex items-center justify-center py-4 opacity-70">
                  <Loader2 size={16} className="animate-spin" />
                </div>
              )}

              {!buscando && busca.trim().length >= 2 && resultados.length === 0 && (
                <div className="text-center py-6 opacity-60 text-sm">
                  Nenhum cliente encontrado.
                  <button
                    type="button"
                    onClick={() => { setNome(busca); setModo('novo'); }}
                    className="block mx-auto mt-2 text-blue-600 hover:underline text-xs"
                  >
                    Cadastrar "{busca}" como novo cliente
                  </button>
                </div>
              )}

              {!buscando && busca.trim().length < 2 && (
                <div className="text-center py-6 opacity-60 text-xs">Digite ao menos 2 caracteres pra buscar.</div>
              )}

              {!buscando && resultados.length > 0 && (
                <div ref={teclado.listaRef} className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
                  {resultados.map((c, idx) => (
                    <button
                      key={c.id}
                      data-idx={idx}
                      type="button"
                      onClick={() => vincular(c.id)}
                      onMouseEnter={() => teclado.setHighlight(idx)}
                      disabled={vinculando !== null}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition disabled:opacity-50 ${idx === teclado.highlight ? 'bg-blue-50' : 'hover:bg-black/5'}`}
                      style={{ border: '1px solid var(--border, rgba(0,0,0,0.08))' }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.nome}</div>
                        <div className="text-[11px] opacity-70 truncate">
                          {c.codigo_legado && <span>#{c.codigo_legado} · </span>}
                          {c.telefone || c.whatsapp || ''}
                          {c.cidade && ` · ${c.cidade}/${c.uf ?? ''}`}
                        </div>
                      </div>
                      {vinculando === c.id ? (
                        <Loader2 size={14} className="animate-spin flex-shrink-0" />
                      ) : (
                        <Check size={14} className="opacity-50 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {modo === 'novo' && conflito && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#fde68a', border: '1px solid #fbbf24' }}>
                <div className="flex items-center gap-1.5 text-xs font-medium text-black/70 mb-1">
                  <Link2 size={12} /> Este WhatsApp ja pertence a um cliente
                </div>
                <div className="text-sm font-medium text-black">{conflito.nome}</div>
                <div className="text-xs text-black/70 space-y-0.5 mt-1">
                  {conflito.codigo_legado && <div>Codigo: {conflito.codigo_legado}</div>}
                  {conflito.cpf_cnpj && <div>CPF/CNPJ: {formatarDoc(conflito.cpf_cnpj)}</div>}
                  {(conflito.telefone || conflito.whatsapp) && <div>Tel: {conflito.telefone || conflito.whatsapp}</div>}
                  {(conflito.cidade || conflito.uf) && <div>{[conflito.cidade, conflito.uf].filter(Boolean).join(' / ')}</div>}
                </div>
              </div>
              <p className="text-[11px] opacity-60">
                Pra evitar cliente duplicado, vincule este contato ao cadastro que ja existe.
              </p>
              <button
                type="button"
                onClick={() => vincular(conflito.id)}
                disabled={vinculando !== null}
                className="w-full py-2 rounded-lg btn-primario flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {vinculando === conflito.id ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                Usar este cliente
              </button>
              <button
                type="button"
                onClick={() => setConflito(null)}
                className="w-full py-1.5 rounded-lg border text-sm font-medium"
                style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
              >
                Voltar e editar
              </button>
            </div>
          )}

          {modo === 'novo' && !conflito && (
            <div className="space-y-2">
              <div>
                <label className="text-xs opacity-70 block mb-0.5">Nome *</label>
                {/* ⚠️ Aqui e' `UpperInput` DE PROPOSITO, ao contrario do nome de CONTATO (que e'
                    caixa livre desde 14/08). Sao entidades diferentes: contato espelha o nome do
                    WhatsApp, cliente e' CADASTRO — sai em orcamento e em nota fiscal, onde o
                    maiusculo e' a regra da casa. Nao "uniformizar" os dois. */}
                <UpperInput
                  value={nome}
                  onChange={setNome}
                  autoFocus
                  className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                  style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs opacity-70 block mb-0.5">Telefone</label>
                  <input
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                    style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                  />
                </div>
                <div>
                  <label className="text-xs opacity-70 block mb-0.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                    style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                  />
                </div>
              </div>
              <p className="text-[11px] opacity-60 mt-1">
                Cadastra cliente do tipo PF e vincula a este contato. CPF e endereco completam depois em Cadastros &gt; Clientes.
              </p>
              <button
                type="button"
                onClick={cadastrarNovo}
                disabled={!podeCadastrarNovo || salvandoNovo}
                className="w-full py-2 rounded-lg btn-primario flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {salvandoNovo ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                {salvandoNovo ? 'Cadastrando...' : 'Cadastrar e vincular'}
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
